import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const positiveNumber = (value, fallback) => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback;

export default function useContinuousReaderPaging({ panelRef, canvasRef, enabled, pageCount, spread,
  rowStride, topPadding = 12, recordId, loading }) {
  const count = Math.max(1, Math.floor(positiveNumber(pageCount, 1)));
  const step = Math.max(1, Math.floor(positiveNumber(spread, 1)));
  const stride = positiveNumber(rowStride, 1);
  const padding = Math.max(0, Number(topPadding) || 0);
  const rowCount = Math.ceil(count / step);
  const live = useRef(null);
  live.current = { enabled, count, step, stride, padding, rowCount };
  const geometry = useRef(null);
  const pageRef = useRef(0);
  const frameRef = useRef(null);
  const [position, setPosition] = useState({ page: 0, firstRow: 0, lastRow: 1 });
  const [badgeVisible, setBadgeVisible] = useState(true);
  const badgeVisibleRef = useRef(true);
  const badgeTimerRef = useRef(null);
  const showBadge = useCallback(() => {
    if (!live.current.enabled) return;
    if (!badgeVisibleRef.current) {
      badgeVisibleRef.current = true;
      setBadgeVisible(true);
    }
    clearTimeout(badgeTimerRef.current);
    badgeTimerRef.current = setTimeout(() => {
      badgeVisibleRef.current = false;
      setBadgeVisible(false);
    }, 1600);
  }, []);

  const measure = useCallback((top = canvasRef.current?.scrollTop || 0) => {
    const state = live.current;
    const height = Math.max(0, canvasRef.current?.clientHeight || 0);
    const scrollMax = Math.max(0, (canvasRef.current?.scrollHeight || 0) - height);
    const topRow = clamp(Math.floor(Math.max(0, top - state.padding) / state.stride), 0, state.rowCount - 1);
    // A tall viewport can show the final row while its top remains in the
    // previous row. At the native bottom, report the final spread consistently.
    const row = scrollMax > 0 && top >= scrollMax - 2 ? state.rowCount - 1 : topRow;
    const bottomRow = Math.floor(Math.max(0, top + height - state.padding - 1) / state.stride);
    const next = { page: row * state.step, firstRow: Math.max(0, topRow - 1),
      lastRow: Math.min(state.rowCount - 1, Math.max(topRow, bottomRow) + 1) };
    if (geometry.current) Object.assign(geometry.current, { top, scrollMax });
    pageRef.current = next.page;
    setPosition((current) => current.page === next.page && current.firstRow === next.firstRow && current.lastRow === next.lastRow ? current : next);
  }, [canvasRef]);

  const moveTo = useCallback((top) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    const hasLayout = canvas.scrollHeight > 0 && canvas.clientHeight > 0;
    const boundedTop = hasLayout ? clamp(top, 0, Math.max(0, canvas.scrollHeight - canvas.clientHeight)) : Math.max(0, top);
    if (typeof canvas.scrollTo === "function") canvas.scrollTo({ top: boundedTop, behavior: "auto" });
    else canvas.scrollTop = boundedTop;
    // Navigation updates its logical page immediately; native scroll events
    // can arrive in a later frame and will reconcile the actual scroll position.
    measure(boundedTop);
  }, [canvasRef, measure]);

  const goToPage = useCallback((updaterOrNumber) => {
    const state = live.current;
    if (!state.enabled) return;
    const requested = typeof updaterOrNumber === "function" ? updaterOrNumber(pageRef.current) : updaterOrNumber;
    if (!Number.isFinite(Number(requested))) return;
    const row = Math.floor(clamp(Math.floor(Number(requested)), 0, state.count - 1) / state.step);
    moveTo(state.padding + row * state.stride);
    showBadge();
  }, [moveTo, showBadge]);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const previous = geometry.current;
    const next = { recordId, count, step, stride, padding };
    geometry.current = next;
    if (previous && previous.recordId !== recordId) {
      moveTo(0);
      return;
    }
    if (previous && (previous.step !== step || previous.stride !== stride || previous.padding !== padding)) {
      if (previous.count === count && previous.scrollMax > 0 && previous.top >= previous.scrollMax - 2) {
        moveTo(Math.max(0, canvas.scrollHeight - canvas.clientHeight));
        return;
      }
      // The browser may already clamp scrollTop after the spacer shrinks.
      // Map from the last position recorded under the previous geometry.
      const top = Math.max(0, previous.top ?? canvas.scrollTop);
      const oldPosition = Math.max(0, top - previous.padding) / previous.stride;
      const oldRow = Math.floor(oldPosition);
      const fraction = oldPosition - oldRow;
      const oldPage = oldRow * previous.step;
      const newRow = clamp(Math.floor(oldPage / step), 0, rowCount - 1);
      moveTo(top === 0 ? 0 : padding + (newRow + fraction) * stride);
      return;
    }
    // Growing batches preserve native scroll position; shrinking page counts
    // clamp only when the current logical row no longer exists.
    const row = Math.floor(Math.max(0, canvas.scrollTop - padding) / stride);
    if (row >= rowCount) moveTo(padding + (rowCount - 1) * stride);
    else measure();
  }, [canvasRef, enabled, recordId, count, step, stride, padding, rowCount, measure, moveTo]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const schedule = () => {
      const snapshot = geometry.current;
      const state = live.current;
      // Record native position before the next frame, without rendering rows.
      // Do not overwrite an old anchor with a layout-clamped value while a
      // new geometry is awaiting its layout effect.
      if (snapshot && snapshot.count === state.count && snapshot.step === state.step &&
        snapshot.stride === state.stride && snapshot.padding === state.padding) snapshot.top = canvas.scrollTop;
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        measure();
        showBadge();
      });
    };
    // Keep all wheel, touch, momentum and scrollbar behavior native.
    canvas.addEventListener("scroll", schedule, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(canvas);
    return () => {
      canvas.removeEventListener("scroll", schedule);
      observer?.disconnect();
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [canvasRef, enabled, recordId, measure, showBadge]);

  useEffect(() => {
    if (enabled) showBadge();
    else {
      clearTimeout(badgeTimerRef.current);
      badgeVisibleRef.current = false;
      setBadgeVisible(false);
    }
    return () => clearTimeout(badgeTimerRef.current);
  }, [enabled, count, step, recordId, showBadge]);

  const page = Math.min(position.page, Math.floor((count - 1) / step) * step);
  const firstRow = clamp(position.firstRow, 0, rowCount - 1);
  const lastRow = clamp(Math.max(firstRow, position.lastRow), firstRow, rowCount - 1);
  const lastPage = Math.min(count, page + step);
  const pageBadge = enabled && badgeVisible ? createPortal(
    <PageBadge role="status" aria-label="目前預覽頁碼" data-record-export-exclude="true">
      Page {page + 1}{lastPage > page + 1 ? `–${lastPage}` : ""} of {count}{loading ? "+" : ""}
    </PageBadge>, panelRef.current?.closest('[role="dialog"]') || document.body,
  ) : null;
  return { page, goToPage, firstRow, lastRow, pageBadge };
}

const PageBadge = styled.div`
  position: fixed; top: max(10px, env(safe-area-inset-top)); left: 50%; transform: translateX(-50%);
  z-index: 1600; padding: 5px 12px; border-radius: 8px;
  background: rgba(70, 70, 70, .76); color: #fff; pointer-events: none;
  font: 500 clamp(15px, 2vw, 23px)/1.25 system-ui, sans-serif;
  white-space: nowrap; box-shadow: 0 2px 8px #0001;
  @media print { display: none; }
`;
